import React from 'react'
import { Button } from '@/components/ui/button'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { StarRating } from '@/components/ui/StarRating'
import { cn } from '@/lib/utils'
import { format, parseISO } from 'date-fns'
import {
	Search,
	Filter,
	ChevronDown,
	MessageSquare,
	Edit2,
	CheckCircle,
	ExternalLink,
} from 'lucide-react'
import type {
	PublicReview,
	PublicReviewsPageData,
} from '@/types/PublicReviewPage.types'
import { useTranslation } from '@/hooks/useTranslation'

import {
	Select,
	SelectContent,
	SelectGroup,
	SelectItem,
	SelectLabel,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select'

interface ReviewsSectionProps {
	programName: string
	allowNewSubmissions: boolean
	widgetIdForSubmissions?: string | null
	reviews: PublicReview[]
	pagination: PublicReviewsPageData['pagination']
	handlePageChange: (newPage: number) => void
	handleWriteReview: () => void
	showContentLoading: boolean
	isPlaceholderData?: boolean
	isLoading?: boolean
	searchTerm: string
	setSearchTerm: (term: string) => void
	isFilterOpen: boolean
	setIsFilterOpen: (isOpen: boolean) => void
	ratingFilter: number | null
	setRatingFilter: (rating: number | null) => void
	sortBy: string
	setSortBy: (sort: string) => void
}

export const PublicReviewPageReviewsSection: React.FC<ReviewsSectionProps> = ({
	programName,
	allowNewSubmissions,
	widgetIdForSubmissions,
	reviews,
	pagination,
	handlePageChange,
	handleWriteReview,
	showContentLoading,
	isPlaceholderData,
	isLoading,
	searchTerm,
	setSearchTerm,
	isFilterOpen,
	setIsFilterOpen,
	ratingFilter,
	setRatingFilter,
	sortBy,
	setSortBy,
}) => {
	const { t } = useTranslation()
	return (
		<section className="lg:col-span-8">
			{/* Filter/Search Bar */}
			<div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm mb-5">
				<div className="flex flex-col gap-3">
					{/* <div className="relative flex-1">
						<div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
							<Search size={16} className="text-gray-400 dark:text-gray-500" />
						</div>
						<input
							type="text"
							placeholder="Search reviews..."
							className="w-full pl-10 pr-4 py-2 border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 rounded-lg focus:ring-2 focus:ring-indigo-200 dark:focus:ring-indigo-500 focus:border-indigo-400 dark:focus:border-indigo-500 outline-none transition-all text-sm"
							value={searchTerm}
							onChange={(e) => setSearchTerm(e.target.value)}
						/>
					</div> */}

					<div className="flex flex-row items-center gap-2 w-full">
						<div className="relative w-1/2">
							<button
								className={cn(
									'w-full px-3 py-2 rounded-lg border flex items-center justify-between gap-2 transition-all text-sm',
									ratingFilter
										? 'border-indigo-400 text-indigo-700 bg-indigo-50 dark:bg-indigo-900/50 dark:text-indigo-300 dark:border-indigo-500'
										: 'border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
								)}
								onClick={() => setIsFilterOpen(!isFilterOpen)}>
								<div className="flex items-center gap-2">
									<Filter size={14} />
									{ratingFilter
										? t('publicReviewPageReviewsSection.stars', { count: ratingFilter })
										: t('publicReviewPageReviewsSection.allRatings')}
								</div>

								<ChevronDown
									size={14}
									className={`transition-transform ${
										isFilterOpen ? 'rotate-180' : ''
									}`}
								/>
							</button>

							{isFilterOpen && (
								<div className="absolute left-0 right-0 mt-2 bg-white dark:bg-gray-700 rounded-lg shadow-xl overflow-hidden z-20 border border-gray-100 dark:border-gray-600 animate-in fade-in slide-in-from-top-5 duration-200">
									<div className="p-1">
										<button
											className={`w-full text-left px-3 py-1.5 text-sm rounded-md ${
												ratingFilter === null
													? 'bg-indigo-50 text-indigo-700 dark:bg-indigo-600 dark:text-white'
													: 'hover:bg-gray-100 dark:hover:bg-gray-600'
											}`}
											onClick={() => {
												setRatingFilter(null)
												setIsFilterOpen(false)
											}}>
											{t('publicReviewPageReviewsSection.allRatings')}
										</button>
										{[5, 4, 3, 2, 1].map((rating) => (
											<button
												key={rating}
												className={`w-full text-left px-3 py-1.5 text-sm rounded-md ${
													ratingFilter === rating
														? 'bg-indigo-50 text-indigo-700 dark:bg-indigo-600 dark:text-white'
														: 'hover:bg-gray-100 dark:hover:bg-gray-600'
												}`}
												onClick={() => {
													setRatingFilter(rating)
													setIsFilterOpen(false)
												}}>
												{t('publicReviewPageReviewsSection.stars', { count: rating })}
											</button>
										))}
									</div>
								</div>
							)}
						</div>

						<Select value={sortBy} onValueChange={(value) => setSortBy(value)}>
							<SelectTrigger className="w-1/2 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 appearance-none cursor-pointer text-sm focus:ring-1 focus:ring-indigo-200 focus:border-indigo-400">
								<SelectValue placeholder={t('publicReviewPageReviewsSection.sortBy')} />
							</SelectTrigger>
							<SelectContent>
								<SelectGroup>
									<SelectLabel>{t('publicReviewPageReviewsSection.sortBy')}</SelectLabel>
									<SelectItem value="newest">{t('publicReviewPageReviewsSection.newestFirst')}</SelectItem>
									<SelectItem value="highest">{t('publicReviewPageReviewsSection.highestRated')}</SelectItem>
									<SelectItem value="lowest">{t('publicReviewPageReviewsSection.lowestRated')}</SelectItem>
								</SelectGroup>
							</SelectContent>
						</Select>
					</div>
				</div>
			</div>
			{showContentLoading && (
				<div className="space-y-4 opacity-50">
					{[...Array(3)].map((_, i) => (
						<div
							key={i}
							className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm animate-pulse">
							<div className="flex justify-between items-start mb-4">
								<div className="flex items-center">
									<div className="w-10 h-10 bg-gray-200 dark:bg-gray-700 rounded-full mr-3"></div>
									<div>
										<div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-24 mb-2"></div>
										<div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-32"></div>
									</div>
								</div>
								<div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-24"></div>
							</div>
							<div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-full mb-2"></div>
							<div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-2"></div>
							<div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
						</div>
					))}
				</div>
			)}

			{!showContentLoading && reviews.length === 0 && (
				<div className="bg-white dark:bg-gray-800 rounded-xl p-8 text-center shadow-sm">
					<MessageSquare className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500 mb-4" />
					<p className="text-lg font-medium text-gray-700 dark:text-gray-300">
						{t('publicReviewPageReviewsSection.noReviewsYet', { programName })}
					</p>
					<p className="text-sm text-muted-foreground dark:text-gray-400">
						{t('publicReviewPageReviewsSection.beFirstToShare')}
					</p>
					{allowNewSubmissions && widgetIdForSubmissions && (
						<Button
							className="mt-6 bg-indigo-600 hover:bg-indigo-700 text-white dark:bg-indigo-500 dark:hover:bg-indigo-600"
							onClick={handleWriteReview}
							disabled={isPlaceholderData || isLoading}>
							<Edit2 className="mr-2 h-4 w-4" /> {t('publicReviewPageReviewsSection.writeFirstReview')}
						</Button>
					)}
				</div>
			)}

			{!showContentLoading && reviews.length > 0 && (
				<div className="space-y-4">
					{reviews.map((review) => (
						<div
							key={review.id}
							className="bg-white dark:bg-gray-800 rounded-xl p-4 md:p-6 shadow-sm transition-all duration-300 hover:shadow-md">
							<div className="flex justify-between items-start mb-4">
								<div className="flex items-center">
									<Avatar className="h-8 w-8 md:h-12 md:w-12 border-2 border-indigo-100 dark:border-indigo-900">
										{review.social_media_link &&
										(review.social_media_link.includes('twitter.com') ||
											review.social_media_link.includes('x.com')) ? (
											<AvatarImage
												src={`https://unavatar.io/twitter/${review.social_media_link.substring(
													review.social_media_link.lastIndexOf('/') + 1
												)}`}
												alt={review.name || 'User Avatar'}
											/>
										) : null}
										<AvatarFallback className="bg-indigo-50 dark:bg-indigo-700 text-indigo-600 dark:text-indigo-300 font-semibold">
											{(review.name || 'A').charAt(0).toUpperCase()}
										</AvatarFallback>
									</Avatar>
									<div className="ml-3">
										<h3 className="text-sm md:text-base font-semibold text-gray-800 dark:text-gray-100">
											{review.name || t('publicReviewPageReviewsSection.anonymous')}
										</h3>
										{review.reporter_company && (
											<p className="text-xs md:text-sm text-gray-500 dark:text-gray-400">
												{review.reporter_company}
											</p>
										)}
									</div>
								</div>
								<StarRating rating={review.score} size="medium" />
							</div>

							<p className="text-sm md:text-base text-gray-700 dark:text-gray-300 mb-4 leading-relaxed whitespace-pre-wrap">
								{review.review_text}
							</p>

							<div className="flex flex-col md:flex-row md:items-center justify-between gap-3 md:gap-0">
								<div className="flex items-center">
									{review.is_verified && (
										<div className="bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 px-2 py-1 rounded-full flex items-center text-xs md:text-sm">
											<CheckCircle size={12} className="mr-1 md:w-4 md:h-4" />
											<span className="font-medium">{t('publicReviewPageReviewsSection.verifiedReview')}</span>
										</div>
									)}
								</div>
								<div className="flex items-center justify-between md:justify-end gap-3 md:gap-4 text-xs md:text-sm">
									<span className="text-gray-400 dark:text-gray-400">
										{format(parseISO(review.created_at), 'MMM d, yyyy')}
									</span>
									{review.social_media_link && (
										<a
											href={review.social_media_link}
											target="_blank"
											rel="noopener noreferrer"
											className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 font-medium flex items-center gap-1 transition-colors">
											{t('publicReviewPageReviewsSection.viewProfile')}
											<ExternalLink size={12} className="md:w-4 md:h-4" />
										</a>
									)}
								</div>
							</div>
						</div>
					))}
				</div>
			)}

			{!showContentLoading && pagination.totalPages > 1 && (
				<div className="flex justify-center items-center gap-2 mt-8">
					<Button
						variant="outline"
						size="sm"
						className="dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
						onClick={() => handlePageChange(pagination.currentPage - 1)}
						disabled={
							pagination.currentPage === 1 || isLoading || isPlaceholderData
						}>
						{t('publicReviewPageReviewsSection.previous')}
					</Button>
					<span className="text-sm text-muted-foreground dark:text-gray-400">
						{t('publicReviewPageReviewsSection.pageOf', { current: pagination.currentPage, total: pagination.totalPages })}
					</span>
					<Button
						variant="outline"
						size="sm"
						className="dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
						onClick={() => handlePageChange(pagination.currentPage + 1)}
						disabled={
							pagination.currentPage === pagination.totalPages ||
							isLoading ||
							isPlaceholderData
						}>
						{t('publicReviewPageReviewsSection.next')}
					</Button>
				</div>
			)}
		</section>
	)
}
